"use client"

import React, { useState, useEffect, useCallback } from 'react'
import { FiSearch, FiRefreshCw, FiUsers, FiUser, FiBriefcase, FiX } from 'react-icons/fi'
import { FaFileExcel, FaSpinner } from 'react-icons/fa'
import toast from 'react-hot-toast'
import axiosInstance from '../../libs/axios'
import { Pagination, PaginationType } from '../../utils/pagination.utils'
import { getAssociationDisplayLabel } from '../../utils/association.utils'
import { useAuth } from '../../provider/auth.provider'
import { ExcelDownload } from '../../utils/excel.utils'

interface UserProject {
  id: number
  project_id: number
  project?: { id: number; project_name: string } | null
}

interface ProjectUser {
  id: number
  first_name: string
  last_name: string
  email: string
  username?: string | null
  employee_code?: string | null
  association_type?: string | null
  status?: string | null
  employment_status?: string | null
  role?: { id: number; name: string } | null
  designation?: { id: number; name: string } | null
  department?: { id: number; name: string } | null
  state?: { id: number; state_name: string; circle_name?: string | null } | null
  district?: { id: number; name: string } | string | null
  user_projects?: UserProject[] | null
  device_version?: string | null
}

const getUserDistrictName = (user: ProjectUser) => {
  const d = user.district
  if (!d) return 'N/A'
  if (typeof d === 'string') return d || 'N/A'
  return d.name || 'N/A'
}

const getEmployeeName = (user: ProjectUser) =>
  [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || 'N/A'

const formatLabel = (value?: string | null) => {
  if (!value) return 'N/A'
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const getStatusBadge = (value?: string | null, type: 'employment' | 'role' = 'role') => {
  const normalized = value || ''
  const styles: Record<string, string> = {
    working: 'bg-green-50 text-green-700 border-green-200',
    not_working: 'bg-amber-50 text-amber-700 border-amber-200',
    left: 'bg-red-50 text-red-700 border-red-200',
    verified: 'bg-green-50 text-green-700 border-green-200',
    initiated: 'bg-blue-50 text-blue-700 border-blue-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    suspended: 'bg-red-50 text-red-700 border-red-200',
    deactivated: 'bg-gray-50 text-gray-700 border-gray-200',
  }

  return (
    <span className={`inline-flex whitespace-nowrap rounded border px-2 py-1 text-xs font-medium ${styles[normalized] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
      {type === 'employment' ? formatLabel(value) : formatLabel(value)}
    </span>
  )
}

const getProjectNames = (user: ProjectUser) =>
  (user.user_projects ?? [])
    .map((up) => up.project?.project_name)
    .filter((value): value is string => Boolean(value && value.trim()))

const ProjectNamesCell: React.FC<{ user: ProjectUser }> = ({ user }) => {
  const names = getProjectNames(user)
  const [expanded, setExpanded] = useState(false)
  const visibleProjects = names.slice(0, 2)

  if (!names.length) {
    return (
      <div className="flex items-start gap-1">
        <FiBriefcase className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
        <span className="text-gray-400">N/A</span>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="flex items-start gap-1">
        <FiBriefcase className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
        <span className="break-words">
          {visibleProjects.join(', ')}
          {names.length > 2 && (
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              className="ml-1 text-xs font-medium text-blue-700 hover:underline"
            >
              +{names.length - 2} more
            </button>
          )}
        </span>
      </div>

      {expanded && names.length > 2 && (
        <div className="absolute left-0 top-6 z-20 w-64 rounded border border-gray-200 bg-white p-3 shadow-lg">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">All Projects</span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX className="w-3 h-3" />
            </button>
          </div>
          <ul className="space-y-1">
            {names.map((name, index) => (
              <li key={`${name}-${index}`} className="border-b border-gray-100 py-0.5 text-xs text-gray-800 last:border-0">
                {name}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const mapUserForExport = (user: ProjectUser): Record<string, string> => ({
  'Employee Name': getEmployeeName(user),
  'User Name': user.username || 'N/A',
  'E-Code': user.employee_code || 'N/A',
  'Email': user.email || 'N/A',
  'Role': user.role?.name || 'N/A',
  'Role Status': formatLabel(user.status),
  'Employment Status': formatLabel(user.employment_status),
  'Designation': user.designation?.name || 'N/A',
  'Department': user.department?.name || 'N/A',
  'Association': getAssociationDisplayLabel(user.association_type || 'N/A'),
  'App Version': user.device_version || 'N/A',
  'Circle / State': user.state?.circle_name || user.state?.state_name || 'N/A',
  'District': getUserDistrictName(user),
  'Projects': getProjectNames(user).join(', ') || 'N/A',
})

const UserRegistrationPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  // Excel export is restricted to Super Admin (role id 1) only.
  const isSuperAdmin = currentUser?.role?.id === 1

  const [users, setUsers] = useState<ProjectUser[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<Record<string, string>[]>([])
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1, limit: 50, total_count: 0, total_pages: 0,
    offset: 0, has_next_page: false, has_prev_page: false,
    next_page: null, prev_page: null,
  })

  // Debounce search
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350)
    return () => clearTimeout(handle)
  }, [searchInput])

  const fetchUsers = useCallback(async (page = 1, limit = 50) => {
    try {
      setLoading(true)
      const params: any = { page, limit }
      if (search) params.search = search
      const res = await axiosInstance.get('/users/my-project-users', { params })
      setUsers(res.data.data.users || [])
      if (res.data.data.pagination) {
        setPagination(res.data.data.pagination)
      }
    } catch (err) {
      console.error('Failed to fetch project users', err)
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    fetchUsers(1, pagination.limit)
  }, [fetchUsers])

  const handlePageChange = (p: number) => fetchUsers(p, pagination.limit)
  const handleLimitChange = (l: number) => fetchUsers(1, l)

  const fetchExportData = async () => {
    if (exporting) return
    try {
      setExporting(true)
      setExportData([])
      const pageSize = 100
      let page = 1
      let hasMore = true
      const allUsers: ProjectUser[] = []

      while (hasMore) {
        const params: any = { page, limit: pageSize }
        if (search) params.search = search
        const res = await axiosInstance.get('/users/my-project-users', { params })
        const fetched: ProjectUser[] = res.data?.data?.users || []
        const pageInfo = res.data?.data?.pagination
        allUsers.push(...fetched)

        const nextPageAvailable = pageInfo?.has_next_page ??
          (pageInfo?.total_pages ? page < pageInfo.total_pages : fetched.length === pageSize)

        if (!fetched.length || !nextPageAvailable) hasMore = false
        else page += 1
      }

      if (!allUsers.length) {
        toast.error('No users found for export')
        return
      }

      setExportData(allUsers.map(mapUserForExport))
      setShowExcelModal(true)
    } catch (err) {
      console.error('Failed to prepare user export', err)
      toast.error('Failed to prepare export data')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <FiUsers className="text-blue-800" />
              User Registration
            </h1>
            <p className="text-sm text-gray-600 mt-1">All users registered under your projects</p>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <button
                onClick={fetchExportData}
                disabled={exporting}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                {exporting ? (
                  <><FaSpinner className="w-4 h-4 animate-spin" /> Preparing...</>
                ) : (
                  <><FaFileExcel className="w-4 h-4" /> Export Excel</>
                )}
              </button>
            )}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, username, code..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-800 w-64"
              />
            </div>
            <button
              onClick={() => { setSearchInput(''); fetchUsers(1, pagination.limit) }}
              className="p-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              title="Reset"
            >
              <FiRefreshCw className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Employee Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">User Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">E-Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Role Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Employment Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Designation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Department</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Association</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">App Version</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Circle / State</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">District</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Projects</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-3"></div>
                      <span className="text-sm text-gray-600">Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center">
                    <FiUsers className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No users found for your projects</p>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <FiUser className="w-4 h-4 text-blue-800" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getEmployeeName(user)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.username || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.employee_code || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.role?.name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{getStatusBadge(user.status, 'role')}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{getStatusBadge(user.employment_status, 'employment')}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.designation?.name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.department?.name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{getAssociationDisplayLabel(user.association_type || 'N/A')}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.device_version || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {user.state?.circle_name || user.state?.state_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{getUserDistrictName(user)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[220px]">
                      <ProjectNamesCell user={user} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
        />
      </div>

      {isSuperAdmin && (
        <ExcelDownload
          isOpen={showExcelModal}
          onClose={() => setShowExcelModal(false)}
          data={exportData}
          filename="user_registration"
          title="Export User Registration"
          description="Download all users registered under your projects"
        />
      )}
    </div>
  )
}

export default UserRegistrationPage
